import { useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Area } from '@domain/entities/Organization';
import { CONFIRMED_ABSENCE_STATUSES } from '@domain/vocabulary';
import { DateRange } from '@domain/value-objects/DateRange';
import { getServices } from '@infrastructure/services';
import {
  Banner,
  Button,
  Card,
  Chip,
  EmptyState,
  Pagination,
  Spinner,
  cssVars,
  cx,
} from '../../design-system';
import { usePagination } from '../../hooks/usePagination';
import { formatDay } from '../../format';
import { useAccess } from '../../hooks/useAccess';
import { usePersonLink } from '../../hooks/usePersonLink';
import { orgChartPath, orgChartState } from '../OrgChart/orgChartLinks';
import styles from './PanoramaScreen.module.css';
import { PageTitle } from '../../shell/PageTitle';

/**
 * El panorama de la empresa — no viene del prototipo.
 *
 * Existe porque el portal sabía contestar «cómo va mi área» y no «cómo va la
 * empresa». El superusuario tenía un «Estado del sistema» que cuenta plantilla,
 * sub áreas y áreas sin administrador: estado de plataforma, no operación. Nadie
 * podía ver de un vistazo quién está fuera hoy en las ocho áreas, que es la
 * pregunta que hace quien dirige.
 *
 * **«Fuera hoy» se cuenta con las ausencias confirmadas, no con las que ocupan
 * calendario**, y la distinción ya costó una ronda de QA en este repositorio:
 * `OCCUPYING_STATUSES` incluye lo que alguien apenas pidió, así que un tablero
 * construido sobre esa lista decía que siete personas estaban fuera el día que
 * había dos. Un panorama que afirma quién no está tiene que hablar de decisiones
 * tomadas. Ver la nota en `CONFIRMED_ABSENCE_STATUSES`.
 *
 * **Cuenta en el servidor, no en el cliente.** Ocho áreas por cuatro cifras son
 * treinta y dos agregados —`count()` de Firestore—, y ninguna trae documentos. La
 * alternativa era leer el padrón entero para medir su longitud, que es la forma
 * de que una pantalla de resumen cueste más que la que muestra el detalle.
 */
export const PanoramaScreen = (): ReactNode => {
  const openPerson = usePersonLink();
  const navigate = useNavigate();
  const { holdsAny } = useAccess();
  const today = getServices().clock.today();

  /*
   * El organigrama de cada área, desde aquí también: es la otra pantalla donde
   * quien ve la empresa entera mira un área. Verlo es por rol y armarlo por
   * persona, así que el botón sale con cualquiera de los dos permisos; si se
   * puede mover o sólo mirar lo decide el propio organigrama.
   */
  const seesOrgChart = holdsAny(['ver_organigrama', 'editar_organigrama']);
  const openOrgChart = (area: Area): void => {
    void navigate(orgChartPath(area.id), {
      state: orgChartState({ path: '/panorama', label: 'Panorama de la empresa' }),
    });
  };

  /*
   * De hoy a seis días: la semana que alguien tiene delante, no la natural.
   *
   * `DateRange.create` devuelve un `Result` porque un rango invertido es un error
   * del dominio; aquí los dos extremos los pone esta pantalla y no puede fallar,
   * pero se desenvuelve igual en vez de forzarlo — el mismo trato que en Inicio.
   */
  const week = useMemo(() => {
    const range = DateRange.create(today, today.addDays(6));
    return range.ok ? range.value : null;
  }, [today]);

  const todayRange = useMemo(() => {
    const range = DateRange.create(today, today);
    return range.ok ? range.value : null;
  }, [today]);

  const areas = useQuery({
    queryKey: ['panorama', 'areas'],
    queryFn: async () => {
      const result = await getServices().organization.listAreas();
      if (!result.ok) throw new Error('No pudimos cargar las áreas.');
      return result.value;
    },
  });

  const areaList = useMemo(
    () => (areas.data ?? []).filter((area) => area.active),
    [areas.data],
  );

  /**
   * Una fila por área, con sus cuatro cifras.
   *
   * Todas las áreas en una sola consulta y no una por tarjeta: son ocho llamadas
   * en paralelo dentro de un `queryFn`, así que la pantalla llega entera o no
   * llega. Ocho tarjetas cargando cada una por su cuenta se pintan en un orden
   * distinto cada vez, y el ojo lee eso como que algo se está moviendo.
   */
  const filas = useQuery({
    queryKey: ['panorama', 'filas', areaList.map((a) => a.id).join(','), today.toISO()],
    enabled: areaList.length > 0 && todayRange !== null && week !== null,
    queryFn: async () => {
      const services = getServices();
      if (todayRange === null || week === null) return [];

      return Promise.all(
        areaList.map(async (area) => {
          const [personas, fueraHoy, fueraSemana, porFirmar, vinculos] = await Promise.all([
            services.people.countByStatus(area.id, 'active'),
            services.requests.count({
              areaId: area.id,
              statuses: CONFIRMED_ABSENCE_STATUSES,
              touching: todayRange,
            }),
            services.requests.count({
              areaId: area.id,
              statuses: CONFIRMED_ABSENCE_STATUSES,
              touching: week,
            }),
            services.requests.count({
              areaId: area.id,
              statuses: ['Por revisar', 'En revisión', 'Visto bueno'],
            }),
            services.leaders.listByArea(area.id),
          ]);

          const conLider = new Set(
            (vinculos.ok ? vinculos.value : []).map((link) => link.personId),
          );

          return {
            area,
            personas: personas.ok ? personas.value : 0,
            fueraHoy: fueraHoy.ok ? fueraHoy.value : 0,
            fueraSemana: fueraSemana.ok ? fueraSemana.value : 0,
            porFirmar: porFirmar.ok ? porFirmar.value : 0,
            conLider: conLider.size,
          };
        }),
      );
    },
  });

  /**
   * Quién está fuera hoy, con nombre.
   *
   * Las cifras dicen cuánto y esto dice quién, que es lo que alguien busca cuando
   * una cifra le llama la atención. Se pide sin acotar a un área —el alcance lo
   * resuelven las reglas, que para este rol son las ocho— y con tope: un día con
   * cincuenta ausencias es una lista que nadie lee, y el número de arriba ya lo
   * dijo.
   */
  const fuera = useQuery({
    queryKey: ['panorama', 'fueraHoy', today.toISO()],
    enabled: todayRange !== null,
    queryFn: async () => {
      if (todayRange === null) return [];
      const result = await getServices().requests.list(
        { statuses: CONFIRMED_ABSENCE_STATUSES, touching: todayRange },
        { limit: 60 },
      );
      return result.ok ? result.value.items : [];
    },
  });
  /* De diez en diez: con ocho áreas, un puente deja a decenas fuera el mismo
     día. El chip de arriba sigue diciendo cuántas son en total. */
  const fueraPager = usePagination(fuera.data ?? []);

  const total = useMemo(() => {
    const filasDatos = filas.data ?? [];
    return {
      personas: filasDatos.reduce((suma, fila) => suma + fila.personas, 0),
      fueraHoy: filasDatos.reduce((suma, fila) => suma + fila.fueraHoy, 0),
      fueraSemana: filasDatos.reduce((suma, fila) => suma + fila.fueraSemana, 0),
      porFirmar: filasDatos.reduce((suma, fila) => suma + fila.porFirmar, 0),
      sinLider: filasDatos.reduce(
        (suma, fila) => suma + Math.max(0, fila.personas - fila.conLider),
        0,
      ),
    };
  }, [filas.data]);

  if (areas.error !== null) {
    return (
      <Card>
        <Banner tone="danger">No pudimos cargar las áreas de la empresa.</Banner>
      </Card>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <PageTitle className={styles.title}>Panorama de la empresa</PageTitle>
        <p className={styles.note}>
          Las {areaList.length} áreas activas al {formatDay(today)}. Quién está fuera, qué
          espera firma y a quién le falta líder.
        </p>
      </header>

      {filas.isPending ? (
        <Card>
          <Spinner label="Cargando el panorama" />
        </Card>
      ) : (
        <>
          <div className={styles.totals}>
            <Total label="Personas activas" value={total.personas} />
            <Total label="Fuera hoy" value={total.fueraHoy} tone="brand" />
            <Total label="Fuera esta semana" value={total.fueraSemana} tone="brand" />
            <Total label="Esperando firma" value={total.porFirmar} tone="warning" />
            {/* Sin líder es deuda del padrón, no operación del día: se enseña
                aparte y en rojo sólo cuando la hay. */}
            <Total label="Sin quién les firme" value={total.sinLider} tone="danger" />
          </div>

          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>Por área</h2>
            <ul className={styles.areas}>
              {(filas.data ?? []).map((fila) => (
                <AreaRow
                  key={fila.area.id}
                  {...fila}
                  onOpenOrgChart={
                    seesOrgChart
                      ? () => {
                          openOrgChart(fila.area);
                        }
                      : null
                  }
                />
              ))}
            </ul>
          </section>

          <section className={styles.panel}>
            <div className={styles.panelHead}>
              <h2 className={styles.panelTitle}>Quién está fuera hoy</h2>
              <Chip
                background="var(--color-brand-bg)"
                foreground="var(--color-brand-ink)"
              >
                {(fuera.data ?? []).length}{' '}
                {(fuera.data ?? []).length === 1 ? 'persona' : 'personas'}
              </Chip>
            </div>

            {fuera.isPending ? (
              <Spinner label="Cargando" />
            ) : (fuera.data ?? []).length === 0 ? (
              <EmptyState
                glyph="·"
                title="Hoy no falta nadie"
                note="Ninguna ausencia aprobada toca el día de hoy en ninguna de las áreas."
              />
            ) : (
              <ul className={styles.outList}>
                {fueraPager.items.map((request) => (
                  <li key={request.id} className={styles.outRow}>
                    <button
                      type="button"
                      className={styles.outWho}
                      onClick={() => {
                        openPerson(request.personId);
                      }}
                    >
                      {request.personName}
                    </button>
                    <span className={styles.outArea}>
                      {areaList.find((area) => area.id === request.areaId)?.name ?? '—'}
                      {request.subAreaName.length > 0 && request.subAreaName !== '—'
                        ? ` · ${request.subAreaName}`
                        : ''}
                    </span>
                    <span className={styles.outWhen}>
                      {formatDay(request.range.start)} – {formatDay(request.range.end)}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <Pagination pager={fueraPager} noun="personas" />
          </section>
        </>
      )}
    </div>
  );
};

const Total = ({
  label,
  value,
  tone = 'plain',
}: {
  readonly label: string;
  readonly value: number;
  readonly tone?: 'plain' | 'brand' | 'warning' | 'danger';
}): ReactNode => (
  <div className={cx(styles.total, value > 0 && styles[tone])}>
    <span className={styles.totalValue}>{value}</span>
    <span className={styles.totalLabel}>{label}</span>
  </div>
);

const AreaRow = ({
  area,
  personas,
  fueraHoy,
  fueraSemana,
  porFirmar,
  conLider,
  onOpenOrgChart,
}: {
  readonly area: Area;
  readonly personas: number;
  readonly fueraHoy: number;
  readonly fueraSemana: number;
  readonly porFirmar: number;
  readonly conLider: number;
  /** `null` para quien no puede ver el organigrama: sin botón. */
  readonly onOpenOrgChart: (() => void) | null;
}): ReactNode => {
  const sinLider = Math.max(0, personas - conLider);

  return (
    <li className={styles.areaRow}>
      <span
        className={styles.dot}
        style={cssVars({ '--dot': area.color })}
        aria-hidden="true"
      />
      <div className={styles.areaName}>
        {area.name}
        <span className={styles.areaPeople}>
          {personas} {personas === 1 ? 'persona' : 'personas'}
        </span>
      </div>

      <Cell value={fueraHoy} label="fuera hoy" />
      <Cell value={fueraSemana} label="esta semana" />
      <Cell value={porFirmar} label="esperan firma" tone={porFirmar > 0 ? 'warning' : 'plain'} />
      <Cell value={sinLider} label="sin líder" tone={sinLider > 0 ? 'danger' : 'plain'} />
      {onOpenOrgChart !== null && (
        <span className={styles.areaAction}>
          <Button
            variant="secondary"
            small
            aria-label={`Organigrama de ${area.name}`}
            onClick={onOpenOrgChart}
          >
            Organigrama
          </Button>
        </span>
      )}
    </li>
  );
};

/*
 * El cero se pinta apagado y no se esconde.
 *
 * Una celda vacía y una celda con cero dicen cosas distintas —«no lo sabemos» y
 * «no hay»— y esta pantalla existe para afirmar la segunda. Es la misma
 * distinción que la ficha hace con «Sin registrar».
 */
const Cell = ({
  value,
  label,
  tone = 'plain',
}: {
  readonly value: number;
  readonly label: string;
  readonly tone?: 'plain' | 'warning' | 'danger';
}): ReactNode => (
  <div className={cx(styles.cell, value === 0 && styles.cellZero, value > 0 && styles[tone])}>
    <span className={styles.cellValue}>{value}</span>
    <span className={styles.cellLabel}>{label}</span>
  </div>
);
